import { useCallback, useEffect, useId, useState } from 'react';

import { formatMetersInputValue } from '../lib/floorPlanDesignerDimensions.js';

import { MIN_WALL_LENGTH_METERS } from '../lib/floorPlanDesignerState.js';

import FloorPlanEditModal from './FloorPlanEditModal.jsx';

import FloorPlanSegmentedToggle from './FloorPlanSegmentedToggle.jsx';



/** @param {string} raw */

function parseMetersValue(raw) {

  const parsed = Number.parseFloat(String(raw).trim());

  return Number.isFinite(parsed) ? parsed : null;

}



/**

 * @param {{

 *   roomName: string;

 *   widthMeters: number;

 *   depthMeters: number;

 *   visibleIn3D: boolean;

 *   onApply: (patch: { name: string; widthMeters: number; depthMeters: number; visibleIn3D: boolean }) => boolean;

 *   onClose: () => void;

 * }} props

 */

function FloorPlanRoomEditPopup({

  roomName,

  widthMeters,

  depthMeters,

  visibleIn3D: initialVisibleIn3D,

  onApply,

  onClose,

}) {

  const nameId = useId();

  const widthId = useId();

  const depthId = useId();



  const [nameValue, setNameValue] = useState(roomName);

  const [widthValue, setWidthValue] = useState(() => formatMetersInputValue(widthMeters));

  const [depthValue, setDepthValue] = useState(() => formatMetersInputValue(depthMeters));

  const [visibleIn3D, setVisibleIn3D] = useState(initialVisibleIn3D ?? true);

  const [error, setError] = useState('');



  useEffect(() => {

    setNameValue(roomName);

    setWidthValue(formatMetersInputValue(widthMeters));

    setDepthValue(formatMetersInputValue(depthMeters));

    setVisibleIn3D(initialVisibleIn3D ?? true);

    setError('');

  }, [roomName, widthMeters, depthMeters, initialVisibleIn3D]);



  const handleApply = useCallback(() => {

    const trimmedName = String(nameValue).trim();

    if (!trimmedName) {

      setError('Room name is required.');

      return;

    }



    const widthM = parseMetersValue(widthValue);

    const depthM = parseMetersValue(depthValue);

    if (widthM === null || depthM === null || widthM < MIN_WALL_LENGTH_METERS || depthM < MIN_WALL_LENGTH_METERS) {

      setError(`Length and breadth must be at least ${MIN_WALL_LENGTH_METERS} m.`);

      return;

    }



    const applied = onApply({

      name: trimmedName,

      widthMeters: widthM,

      depthMeters: depthM,

      visibleIn3D,

    });

    if (!applied) {

      setError('Could not apply room changes.');

      return;

    }

    onClose();

  }, [nameValue, widthValue, depthValue, visibleIn3D, onApply, onClose]);



  return (

    <FloorPlanEditModal

      title="Edit room"

      ariaLabel="Edit room"

      onClose={onClose}

      onApply={handleApply}

      error={error}

    >

      <div className="floor-plan-opening-edit-fields">

        <div className="floor-plan-edit-modal-field-row">

          <div className="floor-plan-edit-modal-field floor-plan-edit-modal-field--full">

            <label className="floor-plan-wall-length-label" htmlFor={nameId}>

              Room name

            </label>

            <input

              id={nameId}

              type="text"

              className="new-2d-project-input"

              value={nameValue}

              onChange={(e) => {

                setNameValue(e.target.value);

                setError('');

              }}

            />

          </div>

        </div>



        <div className="floor-plan-edit-modal-field-row">

          <div className="floor-plan-edit-modal-field">

            <label className="floor-plan-wall-length-label" htmlFor={widthId}>

              Length (m)

            </label>

            <input

              id={widthId}

              type="number"

              min={MIN_WALL_LENGTH_METERS}

              step="0.01"

              className="new-2d-project-input"

              value={widthValue}

              onChange={(e) => {

                setWidthValue(e.target.value);

                setError('');

              }}

            />

          </div>

          <div className="floor-plan-edit-modal-field">

            <label className="floor-plan-wall-length-label" htmlFor={depthId}>

              Breadth / width (m)

            </label>

            <input

              id={depthId}

              type="number"

              min={MIN_WALL_LENGTH_METERS}

              step="0.01"

              className="new-2d-project-input"

              value={depthValue}

              onChange={(e) => {

                setDepthValue(e.target.value);

                setError('');

              }}

            />

          </div>

        </div>



        <div className="floor-plan-edit-modal-field-row">

          <div className="floor-plan-edit-modal-field">

            <span className="floor-plan-wall-length-label">Floor visible in 3D</span>

            <FloorPlanSegmentedToggle

              value={visibleIn3D}

              options={[

                { value: true, label: 'On' },

                { value: false, label: 'Off' },

              ]}

              onChange={(next) => setVisibleIn3D(!!next)}

              ariaLabel="Floor visible in 3D"

            />

          </div>

        </div>

      </div>

    </FloorPlanEditModal>

  );

}



export default FloorPlanRoomEditPopup;

